import * as queryString from 'query-string';
import React, { Component, createRef, RefObject } from 'react';
import cookie from 'react-cookies';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { twMerge } from 'tailwind-merge';

import { OriginArea } from '@videoblocks/events-ts/lib/storyblocks/search/OriginAreaEnum';
import { Telemetry } from '@videoblocks/kafka-rest-client';
import { LegacyVideo as Video } from '@videoblocks/storywind';

import { setPendingPauseAudio } from '../../../../Audio/actions/AudioActions';
import displayEventMediator from '../../../../Events/DisplayEventMediator';
import events from '../../../../Events/Events';
import StockItemDisplayEventFactory from '../../../../Events/StockItemDisplayEventFactory';
import StockItemHoverEventFactory from '../../../../Events/StockItemHoverEventFactory';
import { Folder } from '../../../../Member/Folders/types';
import { selectMemberBins } from '../../../../Member/MemberBins/selectors/foldersSelectors';
import { selectPopUpInstanceSpecificInfo } from '../../../../PopUps/selectors/PopUpsSelectors';
import { STORYBOARD_REQUEST_ID_COOKIE } from '../../../../Storyboards/constants';
import {
  selectHasVideoSubscription,
  selectIsLoggedIn,
  selectIsMobile,
  selectPrimarySubscription,
  selectUseNewDownloadOverlay,
} from '../../../../auth/AuthSelectors';
import { Subscription } from '../../../../auth/AuthTypes';
import { contentTypes } from '../../../../common/Constants';
import ElementDisplayedListener from '../../../../common/ElementDisplayedListener';
import Logger from '../../../../common/Logger';
import SiteConstants from '../../../../common/SiteConstants/SiteConstants';
import Duration from '../../../../common/components/Duration';
import LoadingIndicator from '../../../../common/components/LoadingIndicator';
import { StockDownloadContext } from '../../../../common/context/StockDownloadContext';
import {
  StockItem,
  StockItemContext,
  StockItemFormat,
} from '../../../../common/types/StockItemTypes';
import {
  convertStockItemContextToOriginArea,
  getPreviewUrl,
  isMouseOverElement,
  sendBeacon,
  startRecommendedMusicDrawerTimer,
  stopRecommendedMusicDrawerTimer,
} from '../../../../common/utils';
import { ContentClass } from '../../../Shared/enums';
import { DrawerType } from '../../SearchTypes';
import {
  collapseMoreLikeThisDrawer,
  expandMoreLikeThisDrawer,
  fetchCollapsedSetResults,
  fetchCollapsedSetResultsUsingSimilarItems,
  setSelectedStockItemOptions,
  updateSearchPage,
} from '../../actions/SearchActions';
import { VideoSelectedSearchFilterOptions } from '../../containers/MenuContainerInterfaces';
import {
  selectDrawerIsOpen,
  selectDrawerSelectedItemId,
  selectDrawerType,
  selectIsInVisitorVideoThumbnailExperimentVariantOne,
  selectIsInVisitorVideoThumbnailExperimentVariantTwo,
  selectSearchFeatures,
  selectSearchFilterOptions,
  selectSelectedStockItem,
} from '../../selectors/searchSelectors';
import StockItemCardButtons from './StockItemCardButtons';
import StockItemIcons from './StockItemIcons';
import VideoStockItemFormatSelector from './VideoStockItemFormatSelector';
import {
  DeleteButton,
  DownloadButton,
  FavoriteButton,
  MakerButton,
  RecommendedMusicButton,
  RelatedVideosButtonContainer,
  SimilarButton,
  YellowDownloadCTAButton,
} from './components/Buttons';
import VideoStockItemDownloader from './components/VideoStockItemDownloader';

export type VideoStockItemCardProps = {
  binUniqueId: string;
  classNames: string[];
  containerResponsiveClassNames: string[];
  containPoppers: boolean;
  context: StockItemContext;
  draggableCard: boolean;
  handleLoadingMoreCollapsedStockItems: () => void;
  hideOnThreeColumnLayout: boolean;
  isCollapsedStockItemLoadMoreCard: boolean;
  isSelected: boolean;
  page: number;
  searchLogParameters: object;
  shouldInvertControlsVisibility: boolean;
  shouldShowAddToFavorites: boolean;
  shouldShowDeleteButton: boolean;
  shouldShowDownloadButton: boolean;
  shouldStockItemHaveNewFlag: boolean;
  shouldShowMakerButton: boolean;
  stockItem: StockItem;
  stockItemCardContext?: { title: string; position: number };
  stockItemFormats: StockItemFormat[];
  style: object;
  thumbnailClassNames: string;
  overlayGradientClassNames?: string;
  isLoggedIn: boolean;
  videoSubscription: Subscription;
  primarySubscription: Subscription;
  isMobile: boolean;
  formatSelectorVisible: boolean;
  dispatch: ThunkDispatch<any, any, any>;
  downloadButtonTelemetryAction: () => void;
  makerButtonTelemetryAction: () => void;
  favoriteButtonTelemetryAction: () => void;
  openDetailsInNewWindow: boolean;
  openFoldersInNewWindow: boolean;
  redirectToSignUpLink: boolean;
  selectedFormatName?: string;
  selectedFormatResolution?: string;
  downloadPermissions?: Record<string, boolean>;
  selectedSearchFilterOptions: VideoSelectedSearchFilterOptions;
  drawerIsOpen: boolean;
  drawerSelectedItemId: number;
  drawerType: DrawerType;
  bins: Folder[];
  updateSearchPage: typeof updateSearchPage;
  setSelectedStockItemOptions: typeof setSelectedStockItemOptions;
  collapseMoreLikeThisDrawer: typeof collapseMoreLikeThisDrawer;
  expandMoreLikeThisDrawer: typeof expandMoreLikeThisDrawer;
  fetchCollapsedSetResults: typeof fetchCollapsedSetResults;
  fetchCollapsedSetResultsUsingSimilarItems: typeof fetchCollapsedSetResultsUsingSimilarItems;
  setPendingPauseAudio: typeof setPendingPauseAudio;
  isInThumbnailSimplifyExperiment: boolean;
  shouldUseRemainingUi: boolean;
  useSameShootSameModel: boolean;
  useNewDownloadOverlay: boolean;
  clickOriginArea?: OriginArea;
  isInVisitorVideoThumbnailExperimentVariantOne: boolean;
  isInVisitorVideoThumbnailExperimentVariantTwo: boolean;
  forceLoggedInStyling?: boolean;
};

type State = {
  accumulatedPlayMs: number;
  isDragging: boolean;
  isHovering: boolean;
  isPlaying: boolean;
  playPromiseProcessing: boolean;
  showDeleteTooltip: boolean;
  showDownloadTooltip: boolean;
  showFavoriteTooltip: boolean;
  showMoreLikeThisTooltip: boolean;
  showRecommendedMusicTooltip: boolean;
  hoverStartTime: number;
  playStartTime: number;
  cardInViewport: boolean;
};

class VideoStockItemCard extends Component<VideoStockItemCardProps, State> {
  static defaultProps = {
    classNames: [],
    containPoppers: false,
    containerResponsiveClassNames: [
      'relative',
      'float-left',
      'md:w-1/3',
      'lg:w-1/4',
      'w-5/6',
    ],
    draggableCard: false,
    isCollapsedStockItemLoadMoreCard: false,
    shouldShowAddToFavorites: true,
    shouldShowDownloadButton: true,
    shouldShowDeleteButton: false,
    shouldShowMakerButton: true,
    shouldInvertControlsVisibility: false,
    style: {},
    thumbnailClassNames: '',
    overlayGradientClassNames: '',
    isMobile: false,
    formatSelectorVisible: false,
    downloadButtonTelemetryAction: null,
    makerButtonTelemetryAction: null,
    openDetailsInNewWindow: false,
    openFoldersInNewWindow: false,
  };

  state = {
    accumulatedPlayMs: 0,
    isDragging: false,
    isHovering: false,
    isPlaying: false,
    playPromiseProcessing: false,
    showDeleteTooltip: false,
    showDownloadTooltip: false,
    showFavoriteTooltip: false,
    showMoreLikeThisTooltip: false,
    showRecommendedMusicTooltip: false,
    hoverStartTime: null,
    playStartTime: null,
    cardInViewport: false,
  };

  containerRef: RefObject<HTMLDivElement>;
  videoRef: RefObject<{ videoRef: RefObject<HTMLVideoElement> }>;

  constructor(props) {
    super(props);

    this.containerRef = createRef();
    this.videoRef = createRef();
  }

  componentDidMount() {
    // track drags to tell if a touch is a deliberate or the end of a drag
    this.containerRef.current.addEventListener('touchmove', () => {
      if (!this.state.isDragging) {
        this.setState({
          isDragging: true,
        });
      }
    });

    this.containerRef.current.addEventListener('touchstart', () => {
      if (this.state.isDragging) {
        this.setState({
          isDragging: false,
        });
      }
    });

    // render content when containerRef starts to enter the viewport
    ElementDisplayedListener(
      this.containerRef.current,
      () => {
        this.setState({ cardInViewport: true });
      },
      0.1
    );

    ElementDisplayedListener(this.containerRef.current, () => {
      const context = this.props.stockItemCardContext;

      if (context?.position) {
        displayEventMediator.push(
          StockItemDisplayEventFactory.displayedStockItem(
            this.props.stockItem,
            context.position
          )
        );
      }
    });

    this.shouldShowMoreLikeThisTooltipByDefault();

    // ensure recommended music drawer timer is stopped when navigating away
    window.addEventListener('beforeunload', () => {
      // @ts-ignore
      window.recommendedMusicDrawerTimer?.();
    });
  }

  componentWillUnmount() {
    // ensure we pause music and send audio preview and drawer events if new search is performed / user moves to another page
    this.pauseAudioAndStopRecommendedMusicDrawerTimer();
  }

  render() {
    const {
      isCollapsedStockItemLoadMoreCard,
      stockItem,
      style,
      isInVisitorVideoThumbnailExperimentVariantOne,
      isInVisitorVideoThumbnailExperimentVariantTwo,
      isLoggedIn,
      forceLoggedInStyling,
    } = this.props;
    const { cardInViewport } = this.state;

    return (
      <div
        role="button"
        tabIndex={0}
        className={this.getContainerClassNames()}
        data-stock-id={stockItem.id}
        ref={this.containerRef}
        onMouseEnter={this.handleMouseEnter}
        onFocus={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onBlur={this.handleMouseLeave}
        onClick={this.handleStockItemClick}
        onKeyDown={this.handleStockItemClick}
        style={style}
        itemType="schema.org/VideoObject"
      >
        {cardInViewport && isCollapsedStockItemLoadMoreCard
          ? this.renderCollapsedStockItemLoadMoreCard()
          : isLoggedIn || forceLoggedInStyling
          ? this.renderLoggedInStockItemCard()
          : isInVisitorVideoThumbnailExperimentVariantOne
          ? this.renderLoggedInStockItemCard({
              useIconRelativeButton: true,
              downloadButtonText: 'Get Unlimited Downloads',
              useYellowDownloadCtaButton: true,
            })
          : isInVisitorVideoThumbnailExperimentVariantTwo
          ? this.renderLoggedInStockItemCard({
              useIconRelativeButton: true,
              downloadButtonText: 'Download Now',
              useYellowDownloadCtaButton: true,
            })
          : this.renderNormalStockItemCard()}
      </div>
    );
  }

  handleMouseEnter = () => {
    this.handleIsHovering();
    if (!this.props.isMobile) {
      this.play();
    }
  };

  handleIsHovering = () => {
    if (!this.props.formatSelectorVisible) {
      this.setState({
        isHovering: true,
        hoverStartTime: Date.now(),
      });

      if (this.props.page) {
        this.props.updateSearchPage(this.props.page, this.props.stockItem.id);
      }
    }
  };

  getClickOriginArea = () => {
    const { clickOriginArea, context, drawerType } = this.props;
    if (clickOriginArea) {
      return clickOriginArea;
    }

    switch (context) {
      case StockItemContext.SEARCH:
        return OriginArea.SEARCH;

      case StockItemContext.SIMILAR_CONTENT:
        return OriginArea.STOCK_DETAILS;

      case StockItemContext.DRAWER:
        if (drawerType === DrawerType.MORE_LIKE_THIS) {
          return OriginArea.MORE_LIKE_THIS;
        }
        break;

      default:
        return null;
    }
  };

  handleStockItemClick = (e) => {
    e.stopPropagation();

    const { stockItem, stockItemCardContext } = this.props;
    if (e.target.nodeName === 'VIDEO') {
      const stockItemId = stockItem.id;
      const position = stockItemCardContext && stockItemCardContext.position;
      const urlPath = '/api/video/stockItemClickEvent/details';
      const clickOriginArea = this.getClickOriginArea();

      const beacon = sendBeacon(
        urlPath,
        stockItemId,
        position,
        clickOriginArea
      );

      if (!beacon) {
        Logger.produceDetailsClickEvent(stockItem, position, clickOriginArea);
      }

      this.logHoverDone();
    }
  };

  handleMouseLeave = (e) => {
    /**
     * Click on the favorite button will sometimes trigger
     * a mouseLeave event based on the Popper's positioning,
     * preventing the Popper from appearing. This check blocks
     * that from happening.
     */
    if (
      e?.relatedTarget?.className?.includes?.('tooltip') ||
      e?.target?.className?.includes?.('action-icon')
    ) {
      return;
    }
    this.handleIsNotHovering(e);
    this.pause();
  };

  handleIsNotHovering = (e) => {
    const relatedTarget = e.relatedTarget;
    if (
      this.containerRef.current &&
      (relatedTarget instanceof Node
        ? !this.containerRef.current.contains(e.relatedTarget)
        : true)
    ) {
      this.setState({
        isHovering: false,
        playStartTime: null,
        accumulatedPlayMs: this.state.accumulatedPlayMs + this.playDurationMs(),
      });
      this.logHoverDone();
    }
  };

  handleClickAction = (e) => {
    if (!isMouseOverElement(e?.nativeEvent || e, this.containerRef.current)) {
      this.handleMouseLeave(e);
    }
  };

  renderCollapsedStockItemLoadMoreCard() {
    const {
      handleLoadingMoreCollapsedStockItems,
      shouldStockItemHaveNewFlag,
      stockItem,
      thumbnailClassNames,
    } = this.props;

    return (
      <div
        onClick={handleLoadingMoreCollapsedStockItems}
        onKeyDown={handleLoadingMoreCollapsedStockItems}
        role="button"
        tabIndex={0}
      >
        <div
          className={`box thumbnail faded load-more ${thumbnailClassNames} z-0`}
        >
          <StockItemIcons showNewFlag={shouldStockItemHaveNewFlag} />

          <img
            className="action-icon thumbnail-img"
            src={stockItem.thumbnailUrl}
            alt={stockItem.title}
            loading="eager"
          />

          <div className="controls">{this.renderStockItemTitle()}</div>
        </div>

        <h4 className="faded-thumbnail-text">Load More</h4>
      </div>
    );
  }

  renderNormalStockItemCard() {
    const {
      draggableCard,
      formatSelectorVisible,
      shouldStockItemHaveNewFlag,
      thumbnailClassNames,
    } = this.props;
    const { isHovering, isPlaying } = this.state;

    return (
      <>
        <div className="box thumbnail z-0">
          <div
            className={`thumbnail-img-wrapper overflow-hidden rounded-lg ${thumbnailClassNames}`}
          >
            {formatSelectorVisible && this.renderStockItemFormatSelector()}

            <StockItemIcons showNewFlag={shouldStockItemHaveNewFlag} />
            {draggableCard
              ? this.renderVideo()
              : this.renderVideoWithDetailsLink()}
            <LoadingIndicator show={isHovering && !isPlaying} />
          </div>

          {this.renderButtons()}

          {this.shouldShowSimilarContent()}
        </div>

        {this.renderActionButtons()}
        {this.renderDrawerButtons()}
      </>
    );
  }

  renderLoggedInStockItemCard(
    {
      useIconRelativeButton,
      downloadButtonText,
      useYellowDownloadCtaButton,
    } = {
      useIconRelativeButton: false,
      downloadButtonText: '',
      useYellowDownloadCtaButton: false,
    }
  ) {
    const {
      draggableCard,
      formatSelectorVisible,
      shouldStockItemHaveNewFlag,
      thumbnailClassNames,
      useNewDownloadOverlay,
    } = this.props;
    const { isHovering, isPlaying } = this.state;

    return (
      <>
        <div className="box thumbnail z-0">
          <div
            className={twMerge(
              'thumbnail-img-wrapper overflow-hidden rounded-2xl',
              thumbnailClassNames
            )}
          >
            {formatSelectorVisible &&
              (useNewDownloadOverlay
                ? this.renderStockItemFormatSelectorThumbnailExperiment()
                : this.renderStockItemFormatSelector())}
            <StockItemIcons
              showNewFlag={shouldStockItemHaveNewFlag}
              className={`left-4 h-auto top-4 ${
                isHovering ? 'opacity-0' : 'opacity-100'
              }`}
              iconClassName={
                'bg-white px-1.5 py-1 leading-4 text-gray-900 text-sm'
              }
            />
            {draggableCard
              ? this.renderVideo()
              : this.renderVideoWithDetailsLink()}
            <LoadingIndicator show={isHovering && !isPlaying} />
          </div>
          {/* Overlay with gradient */}
          <div
            className={twMerge(
              'absolute inset-0 flex items-center justify-center rounded-2xl pointer-events-none',
              isHovering ? 'opacity-100' : 'opacity-0',
              this.props.overlayGradientClassNames
            )}
            style={{
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 40%)',
            }}
          ></div>
          {this.renderLoggedInTitle()}
          {!useNewDownloadOverlay && this.renderLoggedInDownloadCancelButtons()}
        </div>
        {this.renderLoggedInButtons({
          useIconRelativeButton,
          useYellowDownloadCtaButton,
          downloadButtonText,
        })}
      </>
    );
  }

  play() {
    if (this.videoRef && this.videoRef.current) {
      const playPromise = this.videoRef.current.videoRef.current.play();
      this.setState({ playPromiseProcessing: true });

      if (playPromise) {
        playPromise
          .then(() => {
            this.setState({ playPromiseProcessing: false });
            if (!this.state.isHovering) {
              this.pause();
            }
            // Playback started!
          })
          .catch((e) => {
            // Playback failed.
            this.setState({ playPromiseProcessing: false });

            // If it is not a DOMException AbortError then retry the playback
            // This error occurs when a video is paused before playback started
            if (!(e instanceof DOMException && e.name === 'AbortError')) {
              if (this.state.isHovering) {
                this.play();
              }

              // Log the exception so we can learn what is being thrown
              // console.error('Playback exception: ', {
              //   name: e.name,
              //   errorType: e,
              // });
            }
          });
      }
    }
  }

  pause() {
    if (
      this.videoRef &&
      this.videoRef.current &&
      !this.state.playPromiseProcessing
    ) {
      this.videoRef.current.videoRef.current.pause();
    }
  }

  renderStockItemFormatSelectorThumbnailExperiment() {
    return (
      <VideoStockItemDownloader
        stockItemFormats={this.props.stockItemFormats}
        onClose={this.hideDownloadButtons}
        onSelect={async (event, format) => {
          this.handleSelectedFormatChanged(
            format.formatName,
            format.resolution
          );
          await this.getPrimaryClickAction()(event, format);
          this.hideDownloadButtons(event);
        }}
      />
    );
  }

  renderStockItemFormatSelector() {
    return (
      <VideoStockItemFormatSelector
        isVisible={this.props.formatSelectorVisible}
        stockItemFormats={this.props.stockItemFormats}
        selectedFormatName={this.props.selectedFormatName}
        selectedFormatResolution={this.props.selectedFormatResolution}
        selectedFormatChanged={(selectedFormatName, selectedFormatResolution) =>
          this.handleSelectedFormatChanged(
            selectedFormatName,
            selectedFormatResolution
          )
        }
        downloadPermissions={this.props.downloadPermissions}
      />
    );
  }

  handleSelectedFormatChanged = (
    selectedFormatName,
    selectedFormatResolution
  ) => {
    this.props.setSelectedStockItemOptions({
      id: this.props.stockItem.id,
      selectedFormatName,
      selectedFormatResolution,
    });
  };

  renderActionButtons() {
    const { isHovering } = this.state;
    const {
      shouldShowAddToFavorites,
      shouldShowDownloadButton,
      downloadButtonTelemetryAction,
      stockItem,
      makerButtonTelemetryAction,
      binUniqueId,
      shouldShowDeleteButton,
      containPoppers,
      openFoldersInNewWindow,
      stockItemFormats,
      favoriteButtonTelemetryAction,
      context,
      isInThumbnailSimplifyExperiment,
    } = this.props;

    return (
      <div
        className={twMerge(
          'action-buttons right-2 flex justify-end space-x-2',
          `${!isHovering ? 'lg:hidden' : ''}`
        )}
      >
        <DownloadButton
          shouldShowDownloadButton={shouldShowDownloadButton}
          downloadButtonTelemetryAction={downloadButtonTelemetryAction}
          onClick={this.getPrimaryClickAction()}
        />
        {shouldShowAddToFavorites && !isInThumbnailSimplifyExperiment && (
          <FavoriteButton
            containPoppers={containPoppers}
            stockItem={stockItem}
            stockItemFormats={stockItemFormats}
            favoriteButtonTelemetryAction={favoriteButtonTelemetryAction}
            openFoldersInNewWindow={openFoldersInNewWindow}
            onClick={this.handleClickAction}
            context={context}
          />
        )}
        <DeleteButton
          binUniqueId={binUniqueId}
          shouldShowDeleteButton={shouldShowDeleteButton}
          stockItem={stockItem}
        />
        {this.shouldShowMakerButton() && (
          <MakerButton
            stockItem={stockItem}
            makerButtonTelemetryAction={makerButtonTelemetryAction}
          />
        )}
      </div>
    );
  }

  renderDrawerButtons() {
    const { stockItem, isInThumbnailSimplifyExperiment } = this.props;
    const { isHovering } = this.state;

    let thumbnailExperimentClasses: string;
    if (isInThumbnailSimplifyExperiment) {
      thumbnailExperimentClasses = twMerge(
        'transition-none',
        isHovering ? 'opacity-100' : 'opacity-0'
      );
    }

    return (
      <div
        className={twMerge(
          'drawer-buttons right-2 flex space-x-2',
          thumbnailExperimentClasses
        )}
      >
        {this.shouldShowSimilarContent() && (
          <SimilarButton
            stockItem={stockItem}
            onClick={this.similarContentAction}
          />
        )}
        {this.shouldShowRecommendedMusic() && (
          <RecommendedMusicButton
            stockItem={stockItem}
            onClick={this.recommendedMusicAction}
          />
        )}
      </div>
    );
  }

  renderButtons(shouldShowTitle = true, className = '') {
    const {
      formatSelectorVisible,
      shouldShowDownloadButton,
      shouldInvertControlsVisibility,
    } = this.props;
    const { isHovering } = this.state;

    const hoverStyle = shouldInvertControlsVisibility ? '' : 'bg-none';
    const nonHoverStyle = shouldInvertControlsVisibility ? 'bg-none' : '';

    return (
      <div
        className={twMerge(
          'controls flex video-title-and-gradient',
          className,
          isHovering ? hoverStyle : nonHoverStyle
        )}
      >
        {shouldShowTitle && this.renderStockItemTitle()}

        {formatSelectorVisible && shouldShowDownloadButton && (
          <StockItemCardButtons
            onPrimaryActionClicked={this.getPrimaryClickAction()}
            onSecondaryActionClicked={this.hideDownloadButtons}
            showCancelLabel={this.props.formatSelectorVisible}
            redirectToSignUpLink={this.props.redirectToSignUpLink}
            detailsUrl={this.props.stockItem.detailsUrl}
          />
        )}
      </div>
    );
  }

  renderLoggedInDownloadCancelButtons() {
    const {
      formatSelectorVisible,
      shouldShowDownloadButton,
      redirectToSignUpLink,
      stockItem,
    } = this.props;

    if (!formatSelectorVisible || !shouldShowDownloadButton) {
      return null;
    }

    return (
      <div className="flex absolute left-0 bottom-0 w-full h-14">
        <StockItemCardButtons
          onPrimaryActionClicked={this.getPrimaryClickAction()}
          onSecondaryActionClicked={this.hideDownloadButtons}
          showCancelLabel={formatSelectorVisible}
          redirectToSignUpLink={redirectToSignUpLink}
          detailsUrl={stockItem.detailsUrl}
        />
      </div>
    );
  }

  renderLoggedInButtons(
    {
      useIconRelativeButton,
      useYellowDownloadCtaButton,
      downloadButtonText,
    } = {
      useIconRelativeButton: false,
      useYellowDownloadCtaButton: false,
      downloadButtonText: '',
    }
  ) {
    const { isHovering } = this.state;
    const {
      shouldShowDownloadButton,
      downloadButtonTelemetryAction,
      stockItem,
      makerButtonTelemetryAction,
      containPoppers,
      openFoldersInNewWindow,
      stockItemFormats,
      favoriteButtonTelemetryAction,
      context,
    } = this.props;

    return (
      <div
        className={twMerge(
          'absolute flex items-center justify-between w-full left-0 bottom-0 px-4 pb-4 space-x-2',
          `${!isHovering ? 'hidden' : ''}`
        )}
      >
        <div className="flex space-x-2">
          {/* Don't show the related video button for stock items in the drawer.*/}
          {this.props.context === StockItemContext.SEARCH && (
            <RelatedVideosButtonContainer
              stockItem={stockItem}
              onClick={this.similarContentAction}
              onlyShowIcon={useIconRelativeButton}
            />
          )}
          <FavoriteButton
            containPoppers={containPoppers}
            stockItem={stockItem}
            stockItemFormats={stockItemFormats}
            favoriteButtonTelemetryAction={favoriteButtonTelemetryAction}
            openFoldersInNewWindow={openFoldersInNewWindow}
            onClick={this.handleClickAction}
            context={context}
            thumbnailExperiment
          />
          {this.shouldShowMakerButton() && (
            <MakerButton
              stockItem={stockItem}
              makerButtonTelemetryAction={makerButtonTelemetryAction}
              thumbnailExperiment
            />
          )}
        </div>
        {useYellowDownloadCtaButton ? (
          <YellowDownloadCTAButton
            downloadButtonTelemetryAction={downloadButtonTelemetryAction}
            onClick={this.getPrimaryClickAction()}
            buttonText={downloadButtonText}
          />
        ) : (
          <DownloadButton
            shouldShowDownloadButton={shouldShowDownloadButton}
            downloadButtonTelemetryAction={downloadButtonTelemetryAction}
            onClick={this.getPrimaryClickAction()}
            thumbnailExperiment
          />
        )}
      </div>
    );
  }

  hideDownloadButtons = (event) => {
    event.preventDefault();

    this.props.setSelectedStockItemOptions({
      formatSelectorVisible: false,
    });

    this.setState({
      isHovering: true,
    });
  };

  renderStockItemTitle() {
    const { shouldInvertControlsVisibility, isInThumbnailSimplifyExperiment } =
      this.props;
    const { duration, title } = this.props.stockItem;
    const { isHovering } = this.state;

    const hoverStyle = shouldInvertControlsVisibility
      ? 'opacity-100'
      : 'opacity-0';
    const nonHoverStyle = shouldInvertControlsVisibility
      ? 'opacity-0'
      : 'opacity-100';

    let thumbnailExperimentClasses: string;
    if (isInThumbnailSimplifyExperiment) {
      thumbnailExperimentClasses = twMerge(
        'transition-none',
        isHovering ? 'opacity-100' : 'opacity-0'
      );
    }

    let widthClass: string;
    if (this.shouldShowSimilarContent() && this.shouldShowRecommendedMusic()) {
      widthClass = 'w-3/5';
    } else if (
      this.shouldShowSimilarContent() ||
      this.shouldShowRecommendedMusic()
    ) {
      widthClass = 'w-5/6';
    } else {
      widthClass = 'w-full';
    }

    return (
      <div
        className={twMerge(
          'item-title flex flex-wrap',
          widthClass,
          isHovering ? hoverStyle : nonHoverStyle,
          thumbnailExperimentClasses
        )}
      >
        {duration && <Duration seconds={duration} />}
        <h3 className="truncate text-sm font-semibold video-card-redesign-title w-full mb-0 mr-4">
          {title}
        </h3>
      </div>
    );
  }

  renderLoggedInTitle() {
    const { shouldInvertControlsVisibility } = this.props;
    const { duration, title } = this.props.stockItem;
    const { isHovering } = this.state;

    const hoverStyle = shouldInvertControlsVisibility
      ? 'opacity-0'
      : 'opacity-100';
    const nonHoverStyle = shouldInvertControlsVisibility
      ? 'opacity-100'
      : 'opacity-0';

    return (
      <div
        className={twMerge(
          `stock-item-title flex space-x-2 items-center absolute top-0 left-0 px-4 pt-4 w-full pointer-events-none`,
          isHovering ? hoverStyle : nonHoverStyle
        )}
      >
        <div
          className={
            'py-0.5 px-1.5 bg-white rounded text-sm leading-5 text-gray-900'
          }
        >
          <Duration seconds={duration} />
        </div>
        <h3 className="truncate font-semibold text-white m-0 text-base leading-5">
          {title}
        </h3>
      </div>
    );
  }

  shouldShowMakerButton() {
    const {
      stockItem,
      shouldShowMakerButton,
      isInThumbnailSimplifyExperiment,
      isInVisitorVideoThumbnailExperimentVariantOne,
      isInVisitorVideoThumbnailExperimentVariantTwo,
    } = this.props;

    return (
      shouldShowMakerButton &&
      !isInThumbnailSimplifyExperiment &&
      (stockItem.contentType === 'footage' ||
        stockItem.contentType === 'motionbackground') &&
      !stockItem.is360 &&
      !this.props.isMobile &&
      !(
        isInVisitorVideoThumbnailExperimentVariantOne ||
        isInVisitorVideoThumbnailExperimentVariantTwo
      )
    );
  }

  shouldShowSimilarContent() {
    // NOTE -- The array of related Items ALWAYS INCLUDES stockItem itself.
    //         So if there is only 1 Item in the collapseSetId array,
    //         then we don't have any related Items.
    //
    // If there are only 2 Items in the collapseSetId array,
    //      then we only have one other related Item,
    //      and we should NOT SHOW any UI related to Collapsed Content.
    return (
      this.props.stockItem.numCollapses > 2 &&
      this.props.context === StockItemContext.SEARCH
    );
  }

  shouldShowRecommendedMusic() {
    const { context, stockItem, isInThumbnailSimplifyExperiment } = this.props;
    return (
      stockItem.contentType === contentTypes.FOOTAGE &&
      stockItem.hasRecommendedMusic &&
      context === StockItemContext.SEARCH &&
      !isInThumbnailSimplifyExperiment
    );
  }

  handleVideoStartedPlaying = () => {
    this.setState({
      isPlaying: true,
      playStartTime: this.state.playStartTime || Date.now(), // Because videos loop, don't reset the playStartTime
    });
  };

  playDurationMs() {
    return this.state.playStartTime ? Date.now() - this.state.playStartTime : 0;
  }

  logHoverDone() {
    if (this.state.hoverStartTime) {
      const hoverDuration = Date.now() - this.state.hoverStartTime;
      const playEnd = this.videoRef.current.videoRef.current.currentTime;

      // Needed to avoid a race condition because we both set it and use it here
      const newAccumulatedPlayMs =
        this.state.accumulatedPlayMs + this.playDurationMs();

      const timesLooped = Math.floor(
        newAccumulatedPlayMs / 1000 / this.props.stockItem.duration
      );

      const originArea = convertStockItemContextToOriginArea(
        this.props.context
      );

      let storyboardRequestId: string;
      let storyboardSceneRequestId: string;

      if (originArea === OriginArea.STORYBOARD) {
        storyboardRequestId = cookie.load(STORYBOARD_REQUEST_ID_COOKIE);
        storyboardSceneRequestId = this.context.sceneRequestId;
      }

      const hoverEvent = StockItemHoverEventFactory.create(
        this.props.stockItem,
        this.state.hoverStartTime,
        this.state.playStartTime,
        playEnd,
        timesLooped,
        newAccumulatedPlayMs,
        Date.now(),
        originArea,
        storyboardRequestId,
        storyboardSceneRequestId
      );

      if (hoverDuration > 1000) {
        events.produce(hoverEvent, hoverEvent.user.visitorCookieId);
      }
    }
  }

  async primaryActionDownloadItem(selectedFormat) {
    const { primarySubscription, videoSubscription, context } = this.props;

    const params = this.context; // StockDownloadContext

    const originArea = convertStockItemContextToOriginArea(context);
    if (originArea) {
      params['origin_area'] = originArea;
    }

    Telemetry.increment('download.clicked.stockItemCard');
    const dataBridge = await SiteConstants.buildInstance().getDataBridge();
    await dataBridge.downloadItem(
      queryString.stringifyUrl(
        {
          url: selectedFormat.downloadUrl,
          query: params,
        },
        {
          skipNull: true,
        }
      ),
      queryString.stringifyUrl(
        {
          url: selectedFormat.downloadAjaxUrl,
          query: params,
        },
        {
          skipNull: true,
        }
      ),
      this.props.searchLogParameters,
      this.props.dispatch,
      // NOTE: used to provide data for Intercom in Starter Plan A/B Test, remove after test if possible
      primarySubscription || videoSubscription
    );
  }

  handleOnTouchEnd = () => {
    const { isDragging } = this.state;

    if (!isDragging) {
      const { detailsUrl } = this.props.stockItem;
      window.location.href = detailsUrl;
    }
  };

  handleModalClose = () => {
    this.setState({ renderDownloadModal: false });
  };

  renderVideoWithDetailsLink() {
    const { stockItem } = this.props;

    const src = this.context.sceneRequestId
      ? `${stockItem.detailsUrl}?sceneRequestId=${this.context.sceneRequestId}`
      : stockItem.detailsUrl;

    return (
      <a
        className="image-link"
        href={src}
        onTouchEnd={this.handleOnTouchEnd}
        aria-label={`Go to video details for ${stockItem.title}`}
        target={this.props.openDetailsInNewWindow ? '_blank' : '_self'}
      >
        <div className="overflow-hidden md:rounded-3xl">
          {this.renderVideo()}
        </div>
      </a>
    );
  }

  renderVideo() {
    const { stockItem } = this.props;

    return (
      <Video
        ref={this.videoRef}
        videoUrl={stockItem.previewUrl}
        poster={stockItem.thumbnailUrl}
        onStartedPlaying={this.handleVideoStartedPlaying}
        preload={false}
        className="object-fill h-full"
      />
    );
  }

  getPrimaryClickAction() {
    const {
      stockItem,
      videoSubscription,
      isInVisitorVideoThumbnailExperimentVariantOne,
      isInVisitorVideoThumbnailExperimentVariantTwo,
      isLoggedIn,
    } = this.props;

    if (this.isASubscriptionlessUser()) {
      return () => {
        Telemetry.increment('preview.downloaded.stockItemCard');
        window.location.href = getPreviewUrl(
          stockItem.id,
          stockItem.contentClass
        );
      };
    }

    if (stockItem && !videoSubscription) {
      const visitorDownloadAttemptLink =
        isInVisitorVideoThumbnailExperimentVariantOne ||
        isInVisitorVideoThumbnailExperimentVariantTwo
          ? '/pricing'
          : stockItem.detailsUrl;

      return async (event) => {
        event.preventDefault();
        this.logPrimaryClickAction();
        window.location.href = visitorDownloadAttemptLink;
      };
    }
    return this.getDefaultPrimaryClickAction();
  }

  getDefaultPrimaryClickAction() {
    return async (event, clickedSelectedFormat = null) => {
      this.logPrimaryClickAction();

      if (!this.overrideClickActionsForRedirect()) {
        event.preventDefault();

        if (
          !this.props.formatSelectorVisible &&
          this.props.stockItemFormats.length >= 2
        ) {
          this.primaryActionUpdateSelectedStockItemOption();
        } else {
          const selectedFormat = clickedSelectedFormat
            ? clickedSelectedFormat
            : this.getSelectedFormat();
          await this.primaryActionDownloadItem(selectedFormat);
        }
      }
    };
  }

  getSelectedFormat() {
    const { stockItemFormats, selectedFormatName, selectedFormatResolution } =
      this.props;

    return stockItemFormats.length >= 2 && selectedFormatName
      ? stockItemFormats.find((stockItemFormat) => {
          return (
            stockItemFormat.formatName === selectedFormatName &&
            stockItemFormat.resolution === selectedFormatResolution
          );
        })
      : stockItemFormats[0];
  }

  overrideClickActionsForRedirect() {
    return this.props.redirectToSignUpLink;
  }

  primaryActionUpdateSelectedStockItemOption() {
    this.setState({
      isHovering: false, // Disable hover state when format selector is visible
      isPlaying: false,
    });

    this.props.setSelectedStockItemOptions({
      id: this.props.stockItem.id,
      formatSelectorVisible: true,
      selectedFormatName: this.props.stockItemFormats[0].formatName,
      selectedFormatResolution: this.props.stockItemFormats[0].resolution,
    });
  }

  logPrimaryClickAction() {
    const { stockItem, stockItemCardContext } = this.props;

    const stockItemId = stockItem.id;
    const position = stockItemCardContext && stockItemCardContext.position;
    const urlPath = '/api/video/stockItemClickEvent/download';
    const clickOriginArea = this.getClickOriginArea();

    const beacon = sendBeacon(urlPath, stockItemId, position, clickOriginArea);

    if (!beacon) {
      Logger.produceDownloadClickEvent(stockItem, position, clickOriginArea);
    }
    this.logHoverDone();
  }

  similarContentAction = () => {
    const {
      selectedSearchFilterOptions,
      drawerIsOpen,
      drawerSelectedItemId,
      drawerType,
      stockItem,
      stockItemCardContext,
      isInVisitorVideoThumbnailExperimentVariantOne,
      isInVisitorVideoThumbnailExperimentVariantTwo,
      useSameShootSameModel,
      isLoggedIn,
    } = this.props;

    if (
      drawerIsOpen &&
      (drawerType === DrawerType.MORE_LIKE_THIS ||
        drawerType === DrawerType.SAME_SHOOT_SAME_MODEL_SIMILAR_CLIPS) &&
      drawerSelectedItemId === stockItem.id
    ) {
      this.props.collapseMoreLikeThisDrawer();
    } else if (useSameShootSameModel) {
      this.props.expandMoreLikeThisDrawer({
        initialItemId: stockItem.id,
        drawerType: DrawerType.SAME_SHOOT_SAME_MODEL_SIMILAR_CLIPS,
      });
    } else {
      // ensure we pause audio and stop the suggestions drawer timer if a suggestions drawer is open
      this.pauseAudioAndStopRecommendedMusicDrawerTimer();

      if (
        isInVisitorVideoThumbnailExperimentVariantOne ||
        isInVisitorVideoThumbnailExperimentVariantTwo ||
        isLoggedIn
      ) {
        this.props.fetchCollapsedSetResultsUsingSimilarItems({ stockItem });
      } else {
        const { collapsedSetId, id } = stockItem;
        const updateFields = {
          collapsedSetId,
          id,
          excludeStockItemIds: id,
          page: 1,
        };
        const newSearchOptions =
          selectedSearchFilterOptions.update(updateFields);
        this.props.fetchCollapsedSetResults(newSearchOptions);
      }

      this.props.expandMoreLikeThisDrawer({
        initialItemId: stockItem.id,
        drawerType: DrawerType.MORE_LIKE_THIS,
      });
      Logger.produceRelatedVideoClickEvent(
        stockItem,
        stockItemCardContext && stockItemCardContext.position
      );
    }
  };

  recommendedMusicAction = () => {
    const {
      selectedSearchFilterOptions,
      drawerIsOpen,
      drawerSelectedItemId,
      drawerType,
      stockItem,
    } = this.props;

    // pause audio and stop the timer if the drawer is being closed or if a different suggestions drawer is about to open
    drawerType === DrawerType.RECOMMENDED_MUSIC &&
      this.pauseAudioAndStopRecommendedMusicDrawerTimer();

    if (
      drawerIsOpen &&
      drawerType === DrawerType.RECOMMENDED_MUSIC &&
      drawerSelectedItemId === stockItem.id
    ) {
      this.props.collapseMoreLikeThisDrawer();
    } else {
      Telemetry.increment('search.recommended_music.opened');
      startRecommendedMusicDrawerTimer();

      const { contentType, id } = stockItem;

      const updateFields = {
        contentSuggestions: {
          contentClass: ContentClass.Audio,
          contentType: contentTypes.MUSIC,
          // this an array as we may support multiple sources in the future
          sources: [
            {
              contentType,
              id,
            },
          ],
        },
      };
      // @ts-ignore
      const newSearchOptions = selectedSearchFilterOptions.update(updateFields);
      this.props.fetchCollapsedSetResults(newSearchOptions);
      this.props.expandMoreLikeThisDrawer({
        initialItemId: id,
        drawerType: DrawerType.RECOMMENDED_MUSIC,
      });
    }
  };

  pauseAudioAndStopRecommendedMusicDrawerTimer() {
    this.props.setPendingPauseAudio();
    stopRecommendedMusicDrawerTimer();
  }

  getContainerClassNames() {
    const context = this.props.context;
    return twMerge(
      this.props.containerResponsiveClassNames,
      this.props.classNames,
      'stock-item stock-item-v2',
      this.props.isSelected && 'is-selected',
      this.props.formatSelectorVisible && 'download-format-selector-visible',
      this.props.hideOnThreeColumnLayout && 'hidden-sm hidden-md',
      this.shouldShowSimilarContent() && 'collapsed-card',
      this.props.shouldUseRemainingUi &&
        context !== StockItemContext.DRAWER &&
        'p-0 w-auto md:w-auto lg:w-auto 3xl:w-auto 4xl:w-auto m-0',
      (this.props.isInVisitorVideoThumbnailExperimentVariantOne ||
        this.props.isInVisitorVideoThumbnailExperimentVariantTwo ||
        this.props.forceLoggedInStyling ||
        this.props.isLoggedIn) &&
        context === StockItemContext.DRAWER &&
        'p-0 w-auto md:w-auto lg:w-auto 3xl:w-auto 4xl:w-auto m-0',
      !(
        this.props.isInVisitorVideoThumbnailExperimentVariantOne ||
        this.props.isInVisitorVideoThumbnailExperimentVariantTwo ||
        this.props.forceLoggedInStyling ||
        this.props.isLoggedIn
      ) &&
        context === StockItemContext.DRAWER &&
        'px-2 mb-3'
    );
  }

  isASubscriptionlessUser() {
    return this.props.isLoggedIn && !this.props.videoSubscription;
  }

  shouldShowMoreLikeThisTooltipByDefault() {
    const hasSeenMoreLikeThisTooltip = cookie.load('morelikethis');
    const domain = window && window.location && `.${window.location.hostname}`;

    if (!hasSeenMoreLikeThisTooltip) {
      cookie.save('morelikethis', true, { path: '/', domain });
      this.setState({ showMoreLikeThisTooltip: true });
    }

    this.setState({ showMoreLikeThisTooltip: false });
  }
}

function mapStateToPropsVideo(state, props) {
  const selectedStockItem = selectSelectedStockItem(state);
  const features = selectSearchFeatures(state);
  const { plansAndPricingUpgradeInfo } = selectPopUpInstanceSpecificInfo(state);
  const isStockItemSelected = selectedStockItem.id === props.stockItem.id;

  return {
    isMobile: selectIsMobile(state),
    formatSelectorVisible: isStockItemSelected
      ? selectedStockItem.formatSelectorVisible
      : false,
    selectedFormatName: isStockItemSelected
      ? selectedStockItem.selectedFormatName
      : null,
    selectedFormatResolution: isStockItemSelected
      ? selectedStockItem.selectedFormatResolution
      : null,
    plansAndPricingUpgradeInfo,
    drawerIsOpen: selectDrawerIsOpen(state),
    drawerSelectedItemId: selectDrawerSelectedItemId(state),
    drawerType: selectDrawerType(state),
    selectedSearchFilterOptions: selectSearchFilterOptions(state),
    bins: selectMemberBins(state),
    downloadPermissions: features.downloadPermissions,
    user: state.auth,
    isLoggedIn: selectIsLoggedIn(state),
    videoSubscription: selectHasVideoSubscription(state),
    primarySubscription: selectPrimarySubscription(state),
    isInThumbnailSimplifyExperiment: features.isInThumbnailSimplifyExperiment,
    shouldUseRemainingUi: features.useRemainingSearchUI,
    useSameShootSameModel: features.useSameShootSameModel,
    useNewDownloadOverlay: selectUseNewDownloadOverlay(state),
    isInVisitorVideoThumbnailExperimentVariantOne:
      selectIsInVisitorVideoThumbnailExperimentVariantOne(state),
    isInVisitorVideoThumbnailExperimentVariantTwo:
      selectIsInVisitorVideoThumbnailExperimentVariantTwo(state),
  };
}
VideoStockItemCard.contextType = StockDownloadContext;

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  ...bindActionCreators(
    {
      updateSearchPage,
      setSelectedStockItemOptions,
      collapseMoreLikeThisDrawer,
      expandMoreLikeThisDrawer,
      fetchCollapsedSetResults,
      fetchCollapsedSetResultsUsingSimilarItems,
      setPendingPauseAudio,
    },
    dispatch
  ),
});

export default connect(
  mapStateToPropsVideo,
  mapDispatchToProps
)(VideoStockItemCard);
